import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { type Lang, type TranslationKey, t as translate } from './translations'

interface LangContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  toggleLang: () => void
  t: (key: TranslationKey) => string
}

const LangContext = createContext<LangContextValue | null>(null)

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    // Default to Chinese, persist in localStorage
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('dc-lang') : null
    return (saved === 'en' || saved === 'zh') ? saved : 'zh'
  })

  const handleSetLang = useCallback((newLang: Lang) => {
    setLang(newLang)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('dc-lang', newLang)
    }
  }, [])

  const toggleLang = useCallback(() => {
    handleSetLang(lang === 'zh' ? 'en' : 'zh')
  }, [lang, handleSetLang])

  const t = useCallback((key: TranslationKey) => translate(key, lang), [lang])

  return (
    <LangContext.Provider value={{ lang, setLang: handleSetLang, toggleLang, t }}>
      {children}
    </LangContext.Provider>
  )
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useLang must be used within LangProvider')
  return ctx
}
